import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Assessment,
  AssessmentRecord,
  AssessmentScore,
  AssessmentSubmission,
} from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';

export const assessmentApi = createApi({
  reducerPath: 'assessmentApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.assessments, TagTypes.assessment, TagTypes.assessmentSubmission, TagTypes.assessmentScore],
  endpoints: (builder) => ({
    getAssessments: builder.query<AssessmentRecord[], void>({
      query: () => '/assessment/record',
      providesTags: [TagTypes.assessments],
    }),
    getAssessment: builder.query<Assessment, string>({
      query: (id: string) => `/assessment/${id}`,
      providesTags: (_result, _error, id) => [
        { type: TagTypes.assessment, id },
      ],
    }),
    getAssessmentRecordByAssessmentId: builder.query<AssessmentRecord, string>({
      query: (assessmentId: string) => `/assessment/${assessmentId}/record`,
      providesTags: (_result, _error, assessmentId) => [
        { type: TagTypes.assessment, id: assessmentId },
      ],
    }),
    getAssessmentSubmission: builder.query<AssessmentSubmission, string>({
      query: (recordId: string) => `/assessment/record/${recordId}/submission`,
      providesTags: [TagTypes.assessmentSubmission],
    }),
    sendAssessmentInvites: builder.mutation<
      void,
      { recordId: string; invites: string[] }
    >({
      query: ({ invites, recordId }) => ({
        url: `assessment/record/${recordId}/users`,
        method: 'POST',
        body: invites,
      }),
      invalidatesTags: [TagTypes.assessments],
    }),
    updateAssessmentSubmission: builder.mutation<
      void,
      { submissionId: string; submission: AssessmentSubmission }
    >({
      query: ({ submission, submissionId }) => ({
        url: `/assessment/submission/${submissionId}`,
        method: 'PUT',
        body: submission,
      }),
      invalidatesTags: [TagTypes.assessmentSubmission],
    }),
    completeAssessmentSubmission: builder.mutation<
      void,
      { submissionId: string }
    >({
      query: ({ submissionId }) => ({
        url: `/assessment/submission/${submissionId}/complete`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: [TagTypes.assessmentSubmission, TagTypes.assessments],
    }),
    getAssessmentScore: builder.query<AssessmentScore,string>({
      query: (recordId: string) => ({
        url: `/assessment/record/${recordId}/score`,
        method: 'GET'
      }),
      providesTags: [TagTypes.assessmentScore],
    }),
    getAssessmentScoreByAssessmentId: builder.query<AssessmentScore, string>({
      query: (assessmentId: string) => ({
        url: `/assessment/${assessmentId}/record/score`,
        method: 'GET'
      }),
      providesTags: [TagTypes.assessmentScore],
    }),
  }),
});

export const {
  useGetAssessmentsQuery,
  useGetAssessmentQuery,
  useGetAssessmentRecordByAssessmentIdQuery,
  useLazyGetAssessmentQuery,
  useSendAssessmentInvitesMutation,
  useGetAssessmentSubmissionQuery,
  useUpdateAssessmentSubmissionMutation,
  useCompleteAssessmentSubmissionMutation,
  useGetAssessmentScoreQuery,
  useGetAssessmentScoreByAssessmentIdQuery,
} = assessmentApi;
